import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {fileSizeValidator} from "../../utilityClass/contentIconSize";
import {ContentType} from "../../interface/contentType";
import {ContentResponse} from "../../interface/contentResponse";
import {ContentService} from "../../service/content.service";
import {UserCardUtil} from "../../utilityClass/userCardUtil";
import {MatSnackBar} from "@angular/material/snack-bar";

@Component({
  selector: 'app-add-content-modal',
  templateUrl: './add-content-modal.component.html',
  styleUrl: './add-content-modal.component.scss'
})
export class AddContentModalComponent implements OnInit {
  contentForm!: FormGroup;
  private readonly maxFileSize = 5;
  showFileInput: boolean = false;
  selectedFile: File | any;
  showDashboard: boolean = true;
  showGridList: boolean = false;
  showError: boolean = false
  fileSizeExceeded: boolean = false
  currentImage: any;
  allowedFileTypes: string[] = ['image/jpeg', 'image/png'];

  constructor(private formBuilder: FormBuilder,
              private contentService: ContentService,
              private userCardUtil: UserCardUtil,
              private dialogRef: MatDialogRef<AddContentModalComponent>,
              @Inject(MAT_DIALOG_DATA) private data: { message: string },
              private snackBar: MatSnackBar
  ) {
  }

  ngOnInit() {
    this.initForm();
  }

  getAcceptFileType() {
    return this.allowedFileTypes.join(', ');
  }

  closePopUp() {
    this.dialogRef.close()
  }

  removeImage() {
    const contentIconControl = this.contentForm.get('contentIcon');
    if (contentIconControl) {
      contentIconControl.setValue(null);
      contentIconControl.markAllAsTouched();
    }
    this.showError = true
    this.selectedFile = null;
    this.showFileInput = true;
  }

  onSubmit() {
    this.contentForm.markAllAsTouched()
    if (!this.selectedFile) {
      this.showError = true
    }
    if (this.contentForm.valid && !this.showError) {
      const contentRequest = this.contentForm.value;
      this.contentService.createContent(contentRequest).subscribe(
        (response: ContentResponse) => {
          this.handleFileUpload(response.id);
          this.dialogRef.close(response)
        },
        (error) => {
          this.handleErrorResponse(error)
        }
      );
    }
  }

  getPlatformTypes() {
    return Object.values(ContentType).filter(type => type !== ContentType.CONTACT);
  }

  get editContentControl(): any {
    return this.contentForm.controls;
  }

  onFileSelected(event: any): void {
    const file: File = event.target.files[0];

    if (file) {
      const fileSizeInMB: number = file.size / (1024 * 1024);
      const maxSizeInMB: number = 5;
      const fileType: string = file.type.toLowerCase();
      this.showError = false
      this.showFileInput = false;
      this.fileSizeExceeded = false;
      this.selectedFile = file;

      if (!this.allowedFileTypes.includes(fileType)) {
        event.target.value = '';
        this.showError = true;
        this.selectedFile = null;
        this.showFileInput = true;
        return;
      }

      if (fileSizeInMB > maxSizeInMB && !this.allowedFileTypes.includes(fileType)) {
        event.target.value = '';
        this.fileSizeExceeded = true;
        this.selectedFile = null;
        this.showFileInput = true;
        return;
      }

      this.userCardUtil.resizeImage(file, 500, 500).subscribe({
          next: (result) => {
              this.selectedFile = result;
              this.currentImage = URL.createObjectURL(result);
          },
          error: () => {
              this.showSnackBar('Error resizing image:', 'error')
          }
      })
    }
  }

  getImageAsSource(file: File) {
    return URL.createObjectURL(file);
  }

  private initForm() {
    this.contentForm = this.formBuilder.group({
          name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(20), this.userCardUtil.whitespaceValidator()]],
          contentType: ['', Validators.required],
          contentIcon: [null, [fileSizeValidator(this.maxFileSize)]]
        },
        {
          updateOn: "blur"
        }
    );

    const platformTypeControl = this.contentForm.get('contentType');
    if (platformTypeControl) {
      platformTypeControl.valueChanges.subscribe((platformType) => {
        this.showFileInput = platformType;
      });
    }
  }

  private handleErrorResponse(error: any) {
    if (error.status === 500) {
      this.showSnackBar('Internal server error.', 'error');
    } else {
      this.showSnackBar('An unexpected error occurred.', 'error');
    }
  }

  private showSnackBar(message: string, panelClass: string) {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: [panelClass]
    });
  }

  private handleFileUpload(contentId: bigint) {
    if (this.selectedFile) {
      this.contentService.uploadContentIcon(contentId, this.selectedFile).subscribe(
          () => {
            this.handleUploadSuccess();
          },
          (uploadError) => {
            this.handleErrorResponse(uploadError)
          }
      );
    }
  }

  private handleUploadSuccess() {
    this.showGridList = false;
    this.contentForm.reset();
    this.showDashboard = true;
  }

}
